/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import Page from "components/common_components/Page/Page";
import Form from "./Form";
import { useDispatch } from "react-redux";
import { notifySuccess, notifyError, notifyInfo } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import Icon from "components/common_components/Icon";
import Button from "components/common_components/Button";
import SettingsContext from "components/Settings/SettingsContext";

const headCells = [
    { index: 1, id: "id", label: "Identifier" },
    { index: 2, id: "type", label: "Type" },
];

function RequestTypes(props) {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [resources, setResources] = useState({ evidenceTypes: [] });
    const [mode, setMode] = useState("listOnly");
    const [itemToEdit, setItemToEdit] = useState(undefined);
    const [pageableData, setPageableData] = useState(undefined);

    const {
        ChainOfCustody: { RequestTypes, EvidenceTypes },
    } = useContext(SettingsContext);

    function getTypes(requestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        RequestTypes.getList({
            onSuccess: (data, pageableData) => {
                setData(data);
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                dispatch(notifyInfo(info));
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    function getEvidenceTypes() {
        setIsLoading(true);
        EvidenceTypes.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    evidenceTypes: data,
                }));
                setIsLoading(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    evidenceTypes: [],
                }));
                dispatch(notifyError(error));
                setIsLoading(false);
            },
        });
    }

    useEffect(() => {
        getEvidenceTypes();

        return () => {
            setData([]);
            setItemToEdit(undefined);
        };
    }, []);

    useEffect(() => {
        if (resources.evidenceTypes.length > 0) {
            getTypes();
        }
    }, [resources.evidenceTypes.length]);

    function onRowClickHandler(item) {
        setItemToEdit(item);
        setMode("update");
    }

    function onCancelHandler() {
        setItemToEdit(undefined);
        setMode("listOnly");
    }

    function onSuccess(_, setSubmitting) {
        setSubmitting(false);
        onCancelHandler();
        dispatch(notifySuccess(`Request Type ${mode === "add" ? "created" : "updated"} successfully!`));
        getTypes();
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            onCancelHandler();
            dispatch(notifyError(error));
        }
        setSubmitting(false);
    }

    function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const {
            id,
            type,
            evidenceDocTypes,
            overrides,
            dueInterval,
            dueUnitOfTime,
            notifyBeforeDue,
            notifyBeforeDueInterval,
            notifyBeforeDueUnitOfTime,
            notifyClientOnClose,
        } = values;

        const apiCall = mode === "add" ? RequestTypes.add : RequestTypes.update;

        const evidenceDocTypesList = evidenceDocTypes.map((evidenceType) => ({
            id: mode === "add" ? null : evidenceType.id,
            settingsCocRequestTypesId: evidenceType.settingsCocRequestTypesId,
            settingsCocEvidenceDocTypesId: evidenceType.settingsCocEvidenceDocTypesId,
            descriptionOverride: overrides[evidenceType.settingsCocEvidenceDocTypesId]?.description || evidenceType.descriptionOverride,
        }));

        apiCall({
            payload: {
                id: id.trim().toUpperCase(),
                type,
                evidenceDocTypes: evidenceDocTypesList,
                dueInterval,
                dueUnitOfTime,
                notifyBeforeDue,
                notifyBeforeDueInterval,
                notifyBeforeDueUnitOfTime,
                notifyClientOnClose,
            },
            onSuccess: (response) => onSuccess(response, setSubmitting),
            onError: (error) => onError(error, setSubmitting, setErrors),
        });
    }

    function onDeleteSuccess() {
        dispatch(notifyInfo("Removed successfully!"));
        getTypes();
    }

    function onDeleteError(error) {
        dispatch(notifyError(error.message));
    }

    function onDeleteHandler(selectedItems) {
        RequestTypes.deleteAll({
            ids: selectedItems,
            onSuccess: onDeleteSuccess,
            onError: onDeleteError,
        });
    }

    function filterBuilder(filterValue) {
        return filterValue
            ? {
                  id: filterValue,
                  type: filterValue,
              }
            : {};
    }

    function onAddHandler() {
        setItemToEdit(undefined);
        setMode("add");
    }

    return (
        <Page className="coc-request-types-page">
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    {mode !== "listOnly" && (
                        <Form
                            data={itemToEdit}
                            resources={resources}
                            mode={mode}
                            onSubmit={onSubmitHandler}
                            onCancel={onCancelHandler}
                            title={mode === "add" ? "New Request Type" : "Update Request Type"}
                        />
                    )}
                    {mode === "listOnly" && (
                        <>
                            <Button primary onClick={onAddHandler}>
                                <Icon name="add" size="md" color="white" />
                                <span>Add</span>
                            </Button>
                            <EnhancedTable
                                headCells={headCells}
                                data={data}
                                defaultOrder={{
                                    order: "asc",
                                    orderBy: "id",
                                }}
                                selectable
                                keyColumnName="id"
                                onRowClick={onRowClickHandler}
                                onDelete={onDeleteHandler}
                                onRequestData={getTypes}
                                filterBuilder={filterBuilder}
                                pageableData={pageableData}
                                defaultRowsPerPage={10}
                            />
                        </>
                    )}
                </>
            )}
        </Page>
    );
}

export default RequestTypes;
